/**
 * [Phase 2b-D §6.1 R8] Step 5 - Season Ticket Holders (Live Only)
 *
 * Renders only when `league_type === 'LIVE_DRAFT_LIVE_SEASON'`.
 * For all other league types, renders an "N/A - not applicable for this
 * league type" pane.
 *
 * Behavior:
 *   - Pre-launch: invite link not yet available (league row doesn't exist).
 *     Shows a preview of the link template + helpful copy.
 *   - Post-launch: real invite link from `league.invite_code`. Copy button
 *     and QR code modal (R8: react-qr-code).
 */
import { useState } from "react";
import QRCode from "react-qr-code";
import { Copy, Check, QrCode, Users, X } from "lucide-react";

const APP_ORIGIN =
  typeof window !== "undefined" ? window.location.origin : "";

export default function Step5SeasonTicketHolders({ form, leagueId, league }) {
  const liveType = ["LIVE_DRAFT_LIVE_SEASON"].includes(form?._league_type ?? "");
  const [copied, setCopied] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  if (!liveType) {
    return (
      <div data-testid="wizard-step-5" className="space-y-6">
        <header>
          <h2 className="font-display font-black text-3xl uppercase text-[#F5F5F5]">
            Season Ticket Holders
          </h2>
          <p className="font-body text-sm text-[#A0A0A0] mt-1">
            Invite real managers to join your live league.
          </p>
        </header>
        <div className="bg-[#1C1C1C] border border-[#252525] rounded-xl px-5 py-8 text-center">
          <Users size={28} className="mx-auto text-[#A0A0A0] mb-3" />
          <p className="font-display font-bold text-base text-[#F5F5F5]">
            Not applicable for this league type
          </p>
          <p className="font-body text-sm text-[#A0A0A0] mt-1">
            Manager invites are only used for{" "}
            <span className="font-mono text-[#FF6B00]">LIVE_DRAFT_LIVE_SEASON</span>{" "}
            leagues. Mock leagues seat CPU bots automatically in the Draft Lobby.
          </p>
        </div>
      </div>
    );
  }

  const inviteCode =
    league?.invite_code ?? "(generated when you click Launch League)";
  const inviteUrl = leagueId
    ? `${APP_ORIGIN}/join/${inviteCode}`
    : `${APP_ORIGIN}/join/${inviteCode}`;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <div data-testid="wizard-step-5" className="space-y-8">
      <header>
        <h2 className="font-display font-black text-3xl uppercase text-[#F5F5F5]">
          Season Ticket Holders
        </h2>
        <p className="font-body text-sm text-[#A0A0A0] mt-1">
          Send the invite link below to your managers. They'll claim a seat
          when they sign up.
        </p>
      </header>

      <section className="bg-[#1C1C1C] border border-[#252525] rounded-xl p-5">
        <p className="font-display font-bold text-xs uppercase tracking-wider text-[#A0A0A0] mb-2">
          Invite Link
        </p>
        <div className="flex items-center gap-2">
          <code
            data-testid="wizard-step5-invite-link"
            className="flex-1 bg-[#111111] border border-[#252525] rounded-lg px-3 py-2 font-mono text-sm text-[#F5F5F5] break-all"
          >
            {inviteUrl}
          </code>
          <button
            type="button"
            onClick={onCopy}
            disabled={!leagueId}
            data-testid="wizard-step5-copy-btn"
            className="bg-[#252525] hover:bg-[#333] disabled:opacity-40 disabled:cursor-not-allowed text-[#F5F5F5] rounded-lg px-3 py-2 transition-colors"
            title={copied ? "Copied!" : "Copy invite link"}
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
          </button>
          <button
            type="button"
            onClick={() => setQrOpen(true)}
            disabled={!leagueId}
            data-testid="wizard-step5-qr-btn"
            className="bg-[#252525] hover:bg-[#333] disabled:opacity-40 disabled:cursor-not-allowed text-[#F5F5F5] rounded-lg px-3 py-2 transition-colors"
            title="Show QR code"
          >
            <QrCode size={16} />
          </button>
        </div>
        {!leagueId && (
          <p className="font-body text-xs text-[#A0A0A0] mt-3 italic">
            The real link unlocks once you click Launch League. This is a preview.
          </p>
        )}
      </section>

      {/* QR modal */}
      {qrOpen && leagueId && (
        <div
          data-testid="wizard-step5-qr-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
          onClick={() => setQrOpen(false)}
        >
          <div
            className="relative bg-[#1C1C1C] border border-[#252525] rounded-xl p-6 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setQrOpen(false)}
              data-testid="wizard-step5-qr-close"
              className="absolute top-3 right-3 text-[#A0A0A0] hover:text-[#FF6B00]"
            >
              <X size={18} />
            </button>
            <h3 className="font-display font-black uppercase text-xl text-[#F5F5F5] mb-3">
              League Invite QR
            </h3>
            <div className="bg-white rounded-lg p-4 flex items-center justify-center">
              <QRCode value={inviteUrl} size={256} />
            </div>
            <p className="font-body text-xs text-[#A0A0A0] mt-3 text-center">
              Have your STH scan this with their phone camera.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export const validateStep5 = () => null; // optional step
